import { useState, type CSSProperties, type ReactNode } from "react";

/** Portato da `components/core/Card.jsx` del design system. */

/**
 * ── La regola delle superfici ─────────────────────────────────────────────
 *
 * Card **bianche con un bordo**, sempre. Niente ombre, niente sfumature: la
 * pagina è bianca anche lei, quindi il bordo non è decorazione — è l'unica
 * cosa che dice dove una scheda comincia e dove finisce.
 *
 * L'unica deroga è la **lavanda**, per i rari casi in cui una scheda deve
 * staccarsi dalle altre. Lì il bordo **non ci va**: il colore fa già il lavoro
 * di delimitare, e sommarci un contorno darebbe due segnali per la stessa
 * cosa. Vale anche per le altre superfici piene — nera e lime — per la stessa
 * ragione.
 */
const TONES = {
  surface: {
    background: "var(--surface-card)",
    border: "1px solid var(--border-default)",
  },
  sunken: { background: "var(--surface-sunken)", border: "none" },
  dark: { background: "var(--ink-900)", color: "var(--white)", border: "none" },
  lime: { background: "var(--lime-400)", color: "var(--text-on-lime)", border: "none" },
  lavender: { background: "var(--tint-lavender)", border: "none" },
  float: { background: "var(--white)", border: "1px solid var(--border-strong)" },
} satisfies Record<string, CSSProperties>;

export type CardTone = keyof typeof TONES;

export interface CardProps {
  tone?: CardTone;
  padding?: number | string;
  radius?: string;
  interactive?: boolean;
  onClick?: () => void;
  children?: ReactNode;
  style?: CSSProperties;
}

export function Card({
  tone = "surface",
  padding = "var(--card-padding)",
  radius = "var(--radius-card)",
  interactive = false,
  onClick,
  children,
  style,
}: CardProps) {
  const t = TONES[tone];
  const [hover, setHover] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding,
        borderRadius: radius,
        cursor: interactive ? "pointer" : "default",
        transition: "filter var(--duration-fast) var(--ease-standard)",
        filter: interactive && hover ? "brightness(0.985)" : "none",
        ...t,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
